import React, { useState } from 'react';
import { Loader2, Save, Globe, Plane } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { oapi, uid, ymd } from '@/lib/officialsApi';
import { WeeklyAvailability } from './WeeklyAvailability';
import { DateCalendar } from './DateCalendar';
import { DayEditorDialog } from './DayEditorDialog';

const browserTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const ALL_TZ = (() => { try { return Intl.supportedValuesOf('timeZone'); } catch { return [browserTz(), 'UTC']; } })();

const BlockOut = ({ onBlock }) => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const go = () => {
    if (!from || !to || to < from) { toast.error('Pick a start and end date'); return; }
    const days = [];
    const [y, m, d] = from.split('-').map(Number);
    for (let i = 0; i < 62; i += 1) { const k = ymd(new Date(y, m - 1, d + i)); if (k > to) break; days.push(k); }
    onBlock(days);
    setFrom(''); setTo('');
  };
  return (
    <div className="rounded-xl bg-slate-50 border p-3" data-testid="blockout">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Plane className="w-4 h-4 text-[#CF142B]" />Away for a while?</div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-9 w-40" data-testid="blockout-from" />
        <span className="text-slate-400 text-sm">to</span>
        <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-9 w-40" data-testid="blockout-to" />
        <Button variant="outline" size="sm" className="h-9 text-red-700 border-red-200 hover:bg-red-50" onClick={go} data-testid="blockout-btn">Mark unavailable</Button>
      </div>
    </div>
  );
};

export const AvailabilityEditor = ({ availability, onSaved }) => {
  const [entries, setEntries] = useState(() => (availability?.entries || []).map(e => ({ ...e, start: e.start || '', end: e.end || '' })));
  const [tz, setTz] = useState(availability?.timezone || browserTz());
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState(null);
  const update = (fn) => { setEntries(fn); setDirty(true); };
  const tzs = ALL_TZ.includes(tz) ? ALL_TZ : [tz, ...ALL_TZ];
  const block = (days) => update(p => [...p.filter(e => !days.includes(e.date)), ...days.map(date => ({ id: uid(), type: 'unavailable', date, start: '', end: '' }))]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { timezone: tz, entries: entries.map(e => ({ ...e, start: e.start || null, end: e.end || null })) };
      await oapi('/officials/portal/availability', { method: 'PUT', body });
      toast.success('Availability saved. Your league can see it now.');
      setDirty(false);
      onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 pb-20" data-testid="availability-editor">
      <div className="flex flex-wrap items-center gap-3 bg-white border rounded-2xl p-4">
        <Globe className="w-4 h-4 text-slate-500" />
        <span className="text-sm text-slate-600">Times are in</span>
        <select value={tz} onChange={e => { setTz(e.target.value); setDirty(true); }} className="h-9 rounded-md border px-2 text-sm max-w-[220px]" data-testid="availability-timezone">
          {tzs.map(z => <option key={z} value={z}>{z}</option>)}
        </select>
        <Button onClick={save} disabled={busy || !dirty} className="ml-auto bg-[#00247D] hover:bg-[#001A5C]" data-testid="availability-save-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}Save availability
        </Button>
      </div>
      <div className="grid lg:grid-cols-12 gap-6">
        <section className="lg:col-span-6 bg-white border rounded-2xl p-5">
          <h3 className="text-lg font-black uppercase text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>Weekly hours</h3>
          <p className="text-xs text-slate-500">The hours you're usually free each week. Leave a day empty if it varies.</p>
          <WeeklyAvailability entries={entries} update={update} />
        </section>
        <section className="lg:col-span-6 bg-white border rounded-2xl p-5 space-y-4">
          <div>
            <h3 className="text-lg font-black uppercase text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>Specific dates</h3>
            <p className="text-xs text-slate-500">Tap a date to mark it available or unavailable. Dates override your weekly hours.</p>
          </div>
          <DateCalendar entries={entries} onPick={setDay} />
          <BlockOut onBlock={block} />
        </section>
      </div>
      {dirty && (
        <div className="fixed bottom-20 sm:bottom-6 inset-x-0 z-40 flex justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto bg-[#00247D] text-white rounded-full shadow-2xl pl-5 pr-1.5 py-1.5 flex items-center gap-3" data-testid="availability-unsaved-bar">
            <span className="text-sm">You have unsaved changes</span>
            <Button size="sm" onClick={save} disabled={busy} className="rounded-full bg-amber-400 text-[#00247D] hover:bg-amber-300 font-bold" data-testid="availability-save-floating">Save</Button>
          </div>
        </div>
      )}
      <DayEditorDialog date={day} entries={entries} update={update} onClose={() => setDay(null)} />
    </div>
  );
};
